import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Sparkles, TrendingUp, TrendingDown, Loader2, AlertCircle,
  Target, Shield, Zap, CheckCircle, XCircle, RefreshCw,
  DollarSign, Percent, Clock, ArrowRight, Lock
} from 'lucide-react';
import { useUser } from '@clerk/clerk-react';
import { useAppStore } from '../store';
import * as api from '../utils/api';
import { getAdvancedChecklist, getPresets } from '../utils/api';
import { useTranslation } from '../utils/translations';

export default function AIDeepAnalysis() {
  const { t } = useTranslation();
  const { user } = useUser();
  const { selectedPair, prices, leaderboardPreset, clearLeaderboardPreset, activeStrategy } = useAppStore();
  const [isLoading, setIsLoading] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [confluenceData, setConfluenceData] = useState(null);
  const [aiAnalysis, setAiAnalysis] = useState(null);
  const [error, setError] = useState('');
  const [cachedPreset, setCachedPreset] = useState(null);
  const [presetCacheTime, setPresetCacheTime] = useState(0);
  
  // Ref to track if analysis is in progress (survives re-renders)
  const analysisInProgressRef = useRef(false);
  // Ref to store confluence data (survives re-renders)
  const confluenceDataRef = useRef(null);
  // Ref to track component mount status
  const isMountedRef = useRef(true);

  const currentPrice = prices?.find(p => p.symbol === selectedPair)?.bid || 0;
  
  // Get user ID from Clerk
  const userId = user?.id || null;
  
  // Cache preset for 30 seconds to avoid rate limiting
  const CACHE_DURATION = 30000; // 30 seconds
  
  // Get active preset with caching
  // PRIORITY: leaderboardPreset > activeStrategy (from store) > cachedPreset > userPreset > default
  const getActivePreset = async () => {
    // PRIORITY 1: Preset from leaderboard (highest priority for coherence)
    if (leaderboardPreset) {
      const normalized = String(leaderboardPreset).toUpperCase().trim();
      console.log('🎯 Using preset from leaderboard (highest priority):', leaderboardPreset, '-> normalized:', normalized);
      return normalized;
    }
    
    // PRIORITY 2: Use activeStrategy from global store (set by StrategiesPage)
    if (activeStrategy) {
      const normalized = String(activeStrategy).toUpperCase().trim();
      console.log('🎯 Using activeStrategy from global store:', activeStrategy, '-> normalized:', normalized);
      return normalized;
    }
    
    const now = Date.now();
    
    // PRIORITY 3: Use cached preset if still valid
    if (cachedPreset && (now - presetCacheTime) < CACHE_DURATION) {
      const normalized = String(cachedPreset).toUpperCase().trim();
      console.log('Using cached preset:', cachedPreset, '-> normalized:', normalized);
      return normalized;
    }
    
    // PRIORITY 3: Fetch fresh preset from user settings
    let activePreset = 'ICT_CORE'; // Default
    try {
      const presetsResponse = await getPresets(userId);
      const presetsData = presetsResponse.data;
      
      console.log('📋 Fetched presets data:', {
        presets: presetsData.presets?.map(p => ({ id: p.id, name: p.name, is_active: p.is_active })),
        current_preset: presetsData.current_preset
      });
      
      // Find preset with is_active: true, or use current_preset
      if (presetsData.presets) {
        const activePresetObj = presetsData.presets.find(p => p.is_active === true);
        if (activePresetObj) {
          activePreset = activePresetObj.id;
          console.log('📋 Found active preset from presets array:', activePresetObj.id, 'name:', activePresetObj.name);
        } else if (presetsData.current_preset) {
          activePreset = presetsData.current_preset;
          console.log('📋 Using current_preset from response:', activePreset);
        }
      } else if (presetsData.current_preset) {
        activePreset = presetsData.current_preset;
        console.log('📋 Using current_preset (no presets array):', activePreset);
      }
      
      // IMPORTANT: Don't normalize here - use the exact ID from backend
      // The backend might expect the exact case/format it returns
      // We'll normalize only when comparing or sending to API if needed
      console.log('📋 Active preset before normalization:', activePreset, 'type:', typeof activePreset);
      
      // Normalize preset ID to ensure consistency (uppercase, trim)
      // But keep original for API calls if backend is case-sensitive
      const originalPreset = activePreset;
      if (activePreset) {
        activePreset = String(activePreset).toUpperCase().trim();
      }
      console.log('📋 Active preset after normalization:', activePreset, 'original:', originalPreset);
      
      // Cache the preset
      setCachedPreset(activePreset);
      setPresetCacheTime(now);
      console.log('📋 Fetched and cached preset:', activePreset);
    } catch (presetErr) {
      console.warn('Error fetching preset, using default ICT_CORE:', presetErr);
      // If we have a cached preset, use it even if expired
      if (cachedPreset) {
        const normalized = String(cachedPreset).toUpperCase().trim();
        console.log('Using expired cached preset as fallback:', cachedPreset, '-> normalized:', normalized);
        return normalized;
      }
    }
    
    return activePreset;
  };

  // Save signal to history
  const saveSignal = async () => {
    console.log('=== SAVE SIGNAL DEBUG ===');
    console.log('can_save:', aiAnalysis?.can_save);
    console.log('save_data:', aiAnalysis?.save_data);

    if (!aiAnalysis?.can_save) {
      console.error('Cannot save: can_save is false');
      setError('Setup non salvabile.');
      return;
    }

    if (!aiAnalysis?.save_data) {
      console.error('Cannot save: save_data is missing');
      setError('Dati per il salvataggio mancanti');
      return;
    }

    setIsSaving(true);
    setSaveSuccess(false);
    setError('');

    try {
      console.log('Sending to API:', aiAnalysis.save_data);

      // Use window.location for dynamic host
      const apiUrl = `${window.location.protocol}//${window.location.hostname}:8000/api/signals/save`;
      console.log('API URL:', apiUrl);

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(aiAnalysis.save_data)
      });

      console.log('Response status:', response.status);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Response error:', errorText);
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }

      const result = await response.json();
      console.log('Response data:', result);

      if (result.success) {
        setSaveSuccess(true);
        setTimeout(() => setSaveSuccess(false), 3000);
      } else {
        throw new Error(result.detail || result.message || 'Save failed');
      }
    } catch (err) {
      console.error('Error saving signal:', err);
      setError(`Errore: ${err.message}`);
    } finally {
      setIsSaving(false);
    }
  };

  const fetchConfluence = useCallback(async () => {
    if (!selectedPair) return;
    
    setIsLoading(true);
    setError('');
    setAiAnalysis(null);

    try {
      // Get active preset first (same logic as requestAIAnalysis)
      const activePreset = await getActivePreset();
      console.log('📋 Fetching checklist with preset:', activePreset);
      
      // Use advanced checklist for both directions WITH PRESET
      // IMPORTANT: Use the exact preset ID from backend, don't normalize if it might break backend recognition
      // But normalize for consistency in our code
      let presetForApi = activePreset;
      
      // If we have leaderboardPreset, use that (it's already from the leaderboard, so it's correct)
      if (leaderboardPreset) {
        presetForApi = String(leaderboardPreset).toUpperCase().trim();
        console.log('📋 Using leaderboardPreset for API (from scanner):', presetForApi);
      } else {
        // Normalize the preset from getActivePreset
        presetForApi = activePreset ? String(activePreset).toUpperCase().trim() : activePreset;
        console.log('📋 Using preset from getActivePreset for API:', presetForApi, '(normalized from:', activePreset, ')');
      }
      
      console.log('📋 Final preset for checklist API call:', presetForApi);
      
      const [longRes, shortRes] = await Promise.all([
        getAdvancedChecklist({ 
          symbol: selectedPair, 
          direction: 'long',
          preset: presetForApi,
          user_id: userId
        }),
        getAdvancedChecklist({ 
          symbol: selectedPair, 
          direction: 'short',
          preset: presetForApi,
          user_id: userId
        })
      ]);

      const longData = longRes.data;
      const shortData = shortRes.data;

      console.log('📋 Advanced checklist - LONG:', longData);
      console.log('📋 Advanced checklist - SHORT:', shortData);

      // Pick best direction
      let bestData = null;

      if (longData.score >= 0 && !longData.error) {
        bestData = {
          ...longData,
          direction: 'long',
          overall_score: longData.score,
          can_buy: true,
          can_sell: false,
          level: longData.rating || 'GOOD',
          sync_pairs: [],
          tf_trends: {}, // Will be populated from timeframes
          detected_trend: 'bullish'
        };

        // Extract TF trends from timeframes
        if (longData.timeframes) {
          Object.entries(longData.timeframes).forEach(([tf, data]) => {
            if (data.trend > 0) {
              bestData.tf_trends[tf] = 'bullish';
            }
          });
        }
      }

      if (shortData.score >= 0 && !shortData.error) {
        if (!bestData || shortData.score > bestData.overall_score) {
          bestData = {
            ...shortData,
            direction: 'short',
            overall_score: shortData.score,
            can_buy: false,
            can_sell: true,
            level: shortData.rating || 'GOOD',
            sync_pairs: [],
            tf_trends: {},
            detected_trend: 'bearish'
          };

          // Extract TF trends
          if (shortData.timeframes) {
            Object.entries(shortData.timeframes).forEach(([tf, data]) => {
              if (data.trend > 0) {
                bestData.tf_trends[tf] = 'bearish';
              }
            });
          }
        }
      }

      // Add preset to bestData for reference (use leaderboardPreset if available, otherwise activePreset)
      if (bestData) {
        // Priority: leaderboardPreset > activePreset from getActivePreset
        const presetToUse = leaderboardPreset 
          ? String(leaderboardPreset).toUpperCase().trim() 
          : activePreset;
        
        bestData.preset = presetToUse;
        bestData.entry_threshold = longData.entry_threshold || shortData.entry_threshold;
        bestData.max_total = longData.max_total || shortData.max_total;
        
        console.log('📋 Preset priority check:');
        console.log('  - leaderboardPreset:', leaderboardPreset);
        console.log('  - activePreset from getActivePreset:', activePreset);
        console.log('  - Final preset used:', presetToUse);
      }
      
      console.log('📋 Best data selected:', bestData);
      console.log('📋 Checklist preset used:', activePreset);
      console.log('📋 Preset saved in confluenceData:', bestData?.preset);
      console.log('📋 Entry threshold:', bestData?.entry_threshold);
      console.log('📋 Max total:', bestData?.max_total);
      setConfluenceData(bestData);
    } catch (err) {
      console.error('Error fetching confluence:', err);
      setError('Errore nel caricamento dati');
    } finally {
      setIsLoading(false);
    }
  }, [selectedPair, userId, leaderboardPreset, cachedPreset, presetCacheTime]);

  // Invalidate cache when strategy changes
  useEffect(() => {
    const handleStrategyChange = (event) => {
      const newPreset = event.detail?.preset;
      const normalizedPreset = newPreset ? String(newPreset).toUpperCase().trim() : newPreset;
      console.log('🔄 Strategy changed to:', newPreset, '-> normalized:', normalizedPreset, '- Invalidating cache and reloading confluence data');
      setCachedPreset(null);
      setPresetCacheTime(0);
      
      // Clear leaderboard preset when user manually changes strategy (to avoid confusion)
      clearLeaderboardPreset();
      console.log('🔄 Cleared leaderboard preset (user changed strategy manually)');
      
      // Reload confluence data if a pair is selected
      if (selectedPair) {
        console.log('🔄 Reloading confluence data with new preset:', normalizedPreset);
        // Clear current confluence data to force reload
        setConfluenceData(null);
        setAiAnalysis(null); // Also clear AI analysis as it depends on confluence data
        // Small delay to ensure cache is cleared
        setTimeout(() => {
          fetchConfluence();
        }, 100);
      }
    };

    window.addEventListener('strategy-changed', handleStrategyChange);
    return () => {
      window.removeEventListener('strategy-changed', handleStrategyChange);
    };
  }, [selectedPair, fetchConfluence, clearLeaderboardPreset]);

  // Fetch confluence first to check if score >= 65
  useEffect(() => {
    if (selectedPair) {
      // Small delay to ensure leaderboardPreset is set if coming from scanner
      setTimeout(() => {
        fetchConfluence();
      }, 50);
    }
    // Note: fetchConfluence is intentionally excluded from deps to prevent 
    // re-triggering when cache state changes (which would clear aiAnalysis)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPair]);
  
  // Clear leaderboard preset after use (to avoid stale data)
  useEffect(() => {
    return () => {
      // Clear when component unmounts
      clearLeaderboardPreset();
    };
  }, [clearLeaderboardPreset]);

  // Track mount status
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);
  
  // Keep confluenceDataRef in sync
  useEffect(() => {
    confluenceDataRef.current = confluenceData;
  }, [confluenceData]);

  // Request AI Deep Analysis (only if score >= 65)
  const requestAIAnalysis = async () => {
    const timestamp = Date.now();
    console.log('🔘 AI ANALYSIS BUTTON CLICKED - timestamp:', timestamp);
    console.log('🔘 confluenceData:', confluenceData);
    console.log('🔘 confluenceDataRef.current:', confluenceDataRef.current);
    console.log('🔘 analysisInProgressRef.current:', analysisInProgressRef.current);
    console.log('🔘 isAnalyzing state:', isAnalyzing);
    
    // Use ref to check confluence data (more stable than state)
    const currentConfluenceData = confluenceDataRef.current || confluenceData;
    
    if (!currentConfluenceData || currentConfluenceData.overall_score < 65) {
      console.log('🔘 BLOCKED: No confluence data or score < 65');
      console.log('🔘 currentConfluenceData:', currentConfluenceData);
      return;
    }
    
    // Prevent double-clicks using ref (survives re-renders)
    if (analysisInProgressRef.current) {
      console.log('🔘 BLOCKED: Analysis already in progress (ref check)');
      return;
    }
    
    analysisInProgressRef.current = true;
    console.log('🔘 Starting AI Analysis - setting analysisInProgressRef to true');
    
    setIsAnalyzing(true);
    setError('');

    try {
      // 1. Get active preset (with caching)
      // PRIORITY: leaderboardPreset > confluenceData.preset > cachedPreset > userPreset > default
      let activePreset = null;
      
      // First priority: preset from leaderboard (when navigating from SessionScanner)
      if (leaderboardPreset) {
        activePreset = String(leaderboardPreset).toUpperCase().trim();
        console.log('✅ Using preset from leaderboard (highest priority):', leaderboardPreset, '-> normalized:', activePreset);
      } 
      // Second priority: preset from currentConfluenceData (from checklist) - using ref for stability
      else if (currentConfluenceData?.preset) {
        activePreset = String(currentConfluenceData.preset).toUpperCase().trim();
        console.log('✅ Using preset from confluenceData (checklist):', currentConfluenceData.preset, '-> normalized:', activePreset);
      }
      // Third priority: fetch from store/cache
      else {
        activePreset = await getActivePreset();
        console.log('⚠️ No preset in confluenceData or leaderboard, fetched from store:', activePreset);
      }
      
      // Ensure preset is normalized
      const presetForApi = activePreset ? String(activePreset).toUpperCase().trim() : activePreset;
      console.log('📋 Final preset for AI Analysis API:', presetForApi, '(from:', activePreset, ')');
      
      // 2. Make AI analysis request with preset (using currentConfluenceData for stability)
      const requestData = {
        symbol: selectedPair,
        direction: currentConfluenceData.direction,
        score: currentConfluenceData.overall_score,
        trend: currentConfluenceData.detected_trend,
        confirmations: currentConfluenceData.confirmations,
        tf_trends: currentConfluenceData.tf_trends,
        current_price: currentPrice,
        preset: presetForApi, // ← CRITICAL: Preset from checklist or leaderboard (normalized)
        user_id: userId, // ← Also pass user_id for backend
        // Include preset info from checklist for backend validation
        checklist_preset: currentConfluenceData.preset,
        checklist_entry_threshold: currentConfluenceData.entry_threshold,
        checklist_max_total: currentConfluenceData.max_total
      };
      
      console.log('🤖 AI Analysis Request:', JSON.stringify(requestData, null, 2));
      console.log('📊 Active preset being sent:', activePreset);
      console.log('📊 ConfluenceData preset:', currentConfluenceData.preset);
      console.log('📊 ConfluenceData entry_threshold:', currentConfluenceData.entry_threshold);
      console.log('📊 ConfluenceData max_total:', currentConfluenceData.max_total);
      console.log('📊 Leaderboard preset:', leaderboardPreset);
      
      // Verify preset is in request
      if (!requestData.preset) {
        console.error('❌ ERROR: preset is missing from request data!');
      } else {
        console.log('✅ Preset is present in request:', requestData.preset);
      }
      
      const response = await api.getAiDeepAnalysis(requestData);

      if (response?.data) {
        const analysisData = response.data.ai_analysis || response.data;
        console.log('🤖 AI Analysis Response:', {
          strategy: analysisData.strategy,
          preset: analysisData.preset,
          validation: analysisData.validation,
          veto_factors: analysisData.veto_factors
        });
        console.log('Full response:', analysisData);
        
        // Verify coherence with leaderboard preset
        if (leaderboardPreset && analysisData.preset && analysisData.preset !== leaderboardPreset) {
          console.warn(`⚠️ Preset mismatch! Leaderboard: ${leaderboardPreset}, AI Response: ${analysisData.preset}`);
        } else if (leaderboardPreset && analysisData.preset === leaderboardPreset) {
          console.log('✅ COHERENCE: Preset matches leaderboard!');
        }
        
        // Handle veto_factors if present
        if (analysisData.veto_factors) {
          console.log('📊 Veto Factors:', {
            vetoed: analysisData.veto_factors.vetoed,
            veto_reasons: analysisData.veto_factors.veto_reasons,
            rr: analysisData.veto_factors.rr,
            order_block_usage: analysisData.veto_factors.order_block_usage,
            htf_alignment: analysisData.veto_factors.htf_alignment
          });
        
        // No UI trading advice: keep veto info in the details card only
        if (analysisData.veto_factors.vetoed) {
          console.warn('🚫 Vetoed by strategy:', analysisData.veto_factors.veto_reasons || []);
        }
        }
        
        // Clear leaderboard preset after successful analysis
        clearLeaderboardPreset();
        
        // Handle cached response structure - only update if still mounted
        if (isMountedRef.current) {
          console.log('🔘 Setting AI Analysis result (component still mounted)');
          if (response.data.ai_analysis) {
            setAiAnalysis(response.data.ai_analysis);
          } else {
            setAiAnalysis(response.data);
          }
        } else {
          console.log('🔘 Component unmounted, skipping state update');
        }
      }
    } catch (err) {
      console.error('Error getting AI analysis:', err);
      if (isMountedRef.current) {
        setError('Errore nell\'analisi AI. Riprova.');
      }
    } finally {
      console.log('🔘 AI Analysis complete - resetting analysisInProgressRef');
      analysisInProgressRef.current = false;
      if (isMountedRef.current) {
        setIsAnalyzing(false);
      }
    }
  };

  // Calculate score and canAnalyze
  const score = confluenceData?.overall_score || confluenceData?.score || 0;
  const canAnalyze = score >= 65;
  const reactionZones = Array.isArray(aiAnalysis?.reaction_zones) ? aiAnalysis.reaction_zones.slice(0, 3) : [];
  const setupDevelopmentLines = Array.isArray(aiAnalysis?.setup_development?.lines) ? aiAnalysis.setup_development.lines : [];
  const invalidationRules = Array.isArray(aiAnalysis?.invalidation?.rules) ? aiAnalysis.invalidation.rules : [];
  const invalidationLevel = aiAnalysis?.invalidation?.stop_loss_level ?? aiAnalysis?.stop_loss_invalidation ?? aiAnalysis?.stop_loss_wide ?? aiAnalysis?.invalidation_stop;
  const showInvalidationSection = invalidationRules.length > 0 || (invalidationLevel != null && invalidationLevel !== '');

  console.log('AIDeepAnalysis state:', {
    selectedPair,
    hasConfluenceData: !!confluenceData,
    score,
    canAnalyze,
    confluenceData
  });
  const direction = confluenceData?.direction;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="glass-card p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-xl bg-gradient-to-br from-violet-500/20 to-pink-500/20">
              <Sparkles className="w-6 h-6 text-violet-400" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-white">{t('ai_deep_analysis')}</h2>
              <p className="text-xs text-slate-400">
                {t('ai_analysis_qualified_desc')}
              </p>
            </div>
          </div>

          <div className="text-right">
            <div className="text-2xl font-bold text-white">{selectedPair || '-'}</div>
            <div className="text-sm text-slate-400">{currentPrice || '-'}</div>
          </div>
        </div>
      </div>

      {/* Loading State */}
      {isLoading && (
        <div className="glass-card p-8 text-center">
          <Loader2 className="w-8 h-8 text-violet-400 animate-spin mx-auto mb-3" />
          <p className="text-slate-400">{t('loading')} {selectedPair}...</p>
        </div>
      )}

      {/* No Pair Selected */}
      {!selectedPair && !isLoading && (
        <div className="glass-card p-8 text-center">
          <AlertCircle className="w-12 h-12 text-slate-500 mx-auto mb-3" />
          <h3 className="text-lg font-bold text-slate-400">{t('select_pair')}</h3>
          <p className="text-sm text-slate-500 mt-1">
            {t('select_pair_desc')}
          </p>
        </div>
      )}

      {/* Score Not High Enough */}
      {selectedPair && !isLoading && confluenceData && !canAnalyze && (
        <div className="glass-card p-6">
          <div className="flex items-center gap-4 mb-4">
            <div className="p-3 rounded-xl bg-amber-500/20">
              <Lock className="w-8 h-8 text-amber-400" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-amber-400">{t('setup_not_qualified')}</h3>
              <p className="text-sm text-slate-400">
                {t('current_score')}: <span className="font-bold text-white">{score}%</span> -
                {t('need_score')} <span className="font-bold text-emerald-400">65%+</span> {t('for_ai_analysis')}
              </p>
            </div>
          </div>

          <div className="bg-slate-800/50 rounded-xl p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-slate-400">Confluence Score</span>
              <span className="text-sm font-bold text-white">{score}%</span>
            </div>
            <div className="h-3 bg-slate-700 rounded-full overflow-hidden">
              <div
                className={`h-full transition-all duration-500 ${score >= 65 ? 'bg-emerald-500' : 'bg-amber-500'
                  }`}
                style={{ width: `${score}%` }}
              />
            </div>
            <div className="flex justify-between mt-1 text-[10px] text-slate-600">
              <span>0%</span>
              <span className="text-amber-400 font-bold">65% minimo</span>
              <span>100%</span>
            </div>
          </div>

          <div className="mt-4 p-3 bg-slate-800/30 rounded-lg">
            <p className="text-xs text-slate-400">
              💡 <strong>{t('suggestion')}:</strong> {t('use_scanner_tip')}
            </p>
          </div>
        </div>
      )}

      {/* No Valid Setup */}
      {selectedPair && !isLoading && !confluenceData && (
        <div className="glass-card p-6 text-center">
          <XCircle className="w-12 h-12 text-red-400 mx-auto mb-3" />
          <h3 className="text-lg font-bold text-red-400">{t('no_valid_setup')}</h3>
          <p className="text-sm text-slate-400 mt-1">
            {selectedPair} {t('no_consecutive_tf')}
          </p>
          <button
            onClick={fetchConfluence}
            className="mt-4 px-4 py-2 bg-slate-800 text-slate-300 rounded-lg text-sm flex items-center gap-2 mx-auto hover:bg-slate-700"
          >
            <RefreshCw className="w-4 h-4" />
            {t('reload')}
          </button>
        </div>
      )}

      {/* Qualified Setup - Can Analyze */}
      {selectedPair && !isLoading && canAnalyze && (
        <div className="space-y-4">
          {/* Setup Summary */}
          <div className="glass-card p-5">
            <div className="flex items-center justify-between mb-5">
              <div className="flex items-center gap-3.5">
                {direction === 'long' ? (
                  <div className="p-2.5 rounded-xl bg-emerald-500/20 border border-emerald-500/20 shadow-soft">
                    <TrendingUp className="w-6 h-6 text-emerald-400" />
                  </div>
                ) : (
                  <div className="p-2.5 rounded-xl bg-red-500/20 border border-red-500/20 shadow-soft">
                    <TrendingDown className="w-6 h-6 text-red-400" />
                  </div>
                )}
                <div>
                  <div className="flex items-center gap-2.5">
                    <span className="text-lg font-bold text-white tracking-tight">{selectedPair}</span>
                    <span className={`px-2.5 py-1 rounded-lg text-xs font-bold border ${direction === 'long' ? 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30' : 'bg-red-500/20 text-red-300 border-red-500/30'
                      }`}>
                      {direction === 'long' ? 'BUY' : 'SELL'}
                    </span>
                  </div>
                  <p className="text-xs text-slate-400 mt-1 leading-relaxed">
                    {confluenceData.sync_pairs?.map(p => `${p[0]}+${p[1]}`).join(', ')}
                  </p>
                </div>
              </div>

              <div className="text-right">
                <div className={`text-3xl font-bold tracking-tight ${score >= 75 ? 'text-emerald-300' : 'text-emerald-400'
                  }`}>
                  {score}%
                </div>
                <span className={`text-xs px-2.5 py-1 rounded-full font-semibold mt-1 inline-block ${confluenceData.level === 'A+ Setup' ? 'bg-emerald-500 text-white shadow-soft shadow-emerald-500/20' : 'bg-emerald-500/50 text-white'
                  }`}>
                  {confluenceData.level}
                </span>
              </div>
            </div>

            {/* TF Trends */}
            <div className="flex flex-wrap gap-2 mb-5">
              {confluenceData.tf_trends && Object.entries(confluenceData.tf_trends).map(([tf, trend]) => (
                <span
                  key={tf}
                  className={`px-2.5 py-1.5 text-[10px] font-semibold rounded-lg border transition-smooth ${trend === 'bullish' ? 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30' :
                    trend === 'bearish' ? 'bg-red-500/20 text-red-300 border-red-500/30' :
                      'bg-slate-700/50 text-slate-400 border-slate-600/30'
                    }`}
                >
                  {tf} {trend === 'bullish' ? '↑' : trend === 'bearish' ? '↓' : '−'}
                </span>
              ))}
            </div>

            {/* Request AI Analysis Button */}
            {!aiAnalysis && (
              <button
                onClick={requestAIAnalysis}
                disabled={isAnalyzing}
                className="w-full py-3.5 bg-gradient-to-r from-violet-500 to-pink-500 text-white font-bold rounded-xl flex items-center justify-center gap-2.5 hover:from-violet-600 hover:to-pink-600 transition-smooth disabled:opacity-50 active:scale-[0.98] shadow-medium shadow-violet-500/25 hover:shadow-strong hover:shadow-violet-500/30"
              >
                {isAnalyzing ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    {t('analyzing_with_ai')}
                  </>
                ) : (
                  <>
                    <Sparkles className="w-5 h-5" />
                    {t('request_deep_analysis')}
                  </>
                )}
              </button>
            )}
          </div>

          {/* AI Analysis Results */}
          {aiAnalysis && (
            <div className="space-y-4">
              {/* AI Analysis */}
              <div className="glass-card p-5 border-2 border-violet-500/40 shadow-medium shadow-violet-500/10">
                <div className="flex items-center gap-2.5 mb-5">
                  <div className="p-2 rounded-lg bg-violet-500/20 border border-violet-500/20">
                    <Sparkles className="w-5 h-5 text-violet-400" />
                  </div>
                  <h3 className="text-sm font-bold text-violet-300 tracking-wide uppercase">{t('ai_analysis')}</h3>
                </div>

                {/* Adjusted Score */}
                <div className="bg-slate-800/60 rounded-xl p-5 mb-5 border border-slate-700/50 shadow-soft">
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="text-xs text-slate-400">{t('original_score')}</span>
                      <p className="text-xl font-bold text-white">{score}%</p>
                    </div>
                    <ArrowRight className="w-6 h-6 text-slate-500" />
                    <div>
                      <span className="text-xs text-slate-400">{t('ai_adjusted_score')}</span>
                      <p className={`text-xl font-bold ${aiAnalysis.adjusted_score >= score ? 'text-emerald-400' : 'text-amber-400'
                        }`}>
                        {aiAnalysis.adjusted_score}%
                      </p>
                    </div>
                  </div>
                </div>

                {/* Strategy validation (no trading advice) */}
                {aiAnalysis.veto_factors && aiAnalysis.veto_factors.vetoed && (
                  <div className="bg-red-500/20 border-2 border-red-500/50 rounded-xl p-4 mb-4">
                    <div className="flex items-center gap-2 mb-3">
                      <XCircle className="w-5 h-5 text-red-400" />
                      <h4 className="text-sm font-bold text-red-400 uppercase">Validazione strategia</h4>
                    </div>
                    {aiAnalysis.veto_factors.veto_reasons && aiAnalysis.veto_factors.veto_reasons.length > 0 && (
                      <div className="space-y-2 mb-3">
                        <p className="text-xs text-slate-300 mb-2">Condizioni non soddisfatte:</p>
                        {aiAnalysis.veto_factors.veto_reasons.map((reason, i) => (
                          <div key={i} className="flex items-start gap-2 text-sm">
                            <XCircle className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
                            <span className="text-slate-300">{reason}</span>
                          </div>
                        ))}
                      </div>
                    )}
                    {aiAnalysis.veto_factors.rr !== undefined && (
                      <div className="text-xs text-slate-400 mt-2">
                        <span className="font-semibold">RR:</span> {aiAnalysis.veto_factors.rr}
                      </div>
                    )}
                    {aiAnalysis.veto_factors.order_block_usage !== undefined && (
                      <div className="text-xs text-slate-400">
                        <span className="font-semibold">Order Block Usage:</span> {aiAnalysis.veto_factors.order_block_usage}
                      </div>
                    )}
                    {aiAnalysis.veto_factors.htf_alignment !== undefined && (
                      <div className="text-xs text-slate-400">
                        <span className="font-semibold">HTF Alignment:</span> {aiAnalysis.veto_factors.htf_alignment ? 'Yes' : 'No'}
                      </div>
                    )}
                  </div>
                )}

                {/* Veto Factors Info - Show even if not vetoed (for transparency) */}
                {aiAnalysis.veto_factors && !aiAnalysis.veto_factors.vetoed && (
                  <div className="bg-slate-800/30 border border-slate-700/50 rounded-xl p-3 mb-4">
                    <div className="flex items-center gap-2 mb-2">
                      <CheckCircle className="w-4 h-4 text-emerald-400" />
                      <h4 className="text-xs font-bold text-slate-400 uppercase">Strategy Validation</h4>
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      {aiAnalysis.veto_factors.rr !== undefined && (
                        <div>
                          <span className="text-slate-500">RR:</span> <span className="text-slate-300 font-semibold">{aiAnalysis.veto_factors.rr}</span>
                        </div>
                      )}
                      {aiAnalysis.veto_factors.order_block_usage !== undefined && (
                        <div>
                          <span className="text-slate-500">Order Block:</span> <span className="text-slate-300 font-semibold">{aiAnalysis.veto_factors.order_block_usage}</span>
                        </div>
                      )}
                      {aiAnalysis.veto_factors.htf_alignment !== undefined && (
                        <div className="col-span-2">
                          <span className="text-slate-500">HTF Alignment:</span> <span className="text-slate-300 font-semibold">{aiAnalysis.veto_factors.htf_alignment ? 'Yes' : 'No'}</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* AI Commentary */}
                <div className="bg-slate-800/30 rounded-lg p-3 mb-4">
                  <p className="text-sm text-slate-300 leading-relaxed">
                    {aiAnalysis.analysis}
                  </p>
                </div>

                {/* Entry Levels */}
                <div className="grid grid-cols-4 gap-3 mb-5">
                  <div className="bg-slate-800/60 rounded-xl p-4 text-center border border-cyan-500/20 shadow-soft hover:border-cyan-500/40 transition-smooth">
                    <div className="text-[10px] text-slate-400 mb-2 font-semibold uppercase tracking-wider">ENTRY</div>
                    <div className="text-base font-bold text-cyan-300 font-mono">{aiAnalysis.entry}</div>
                  </div>
                  <div className="bg-slate-800/60 rounded-xl p-4 text-center border border-red-500/30 shadow-soft hover:border-red-500/50 transition-smooth">
                    <div className="text-[10px] text-slate-400 mb-2 font-semibold uppercase tracking-wider">STOP LOSS</div>
                    <div className="text-base font-bold text-red-300 font-mono">{aiAnalysis.stop_loss}</div>
                  </div>
                  <div className="bg-slate-800/60 rounded-xl p-4 text-center border border-emerald-500/30 shadow-soft hover:border-emerald-500/50 transition-smooth">
                    <div className="text-[10px] text-slate-400 mb-2 font-semibold uppercase tracking-wider">TAKE PROFIT</div>
                    <div className="text-base font-bold text-emerald-300 font-mono">{aiAnalysis.take_profit}</div>
                  </div>
                  <div className="bg-slate-800/60 rounded-xl p-4 text-center border border-violet-500/30 shadow-soft hover:border-violet-500/50 transition-smooth">
                    <div className="text-[10px] text-slate-400 mb-2 font-semibold uppercase tracking-wider">R:R RATIO</div>
                    <div className="text-base font-bold text-violet-300 font-mono">{aiAnalysis.risk_reward}</div>
                  </div>
                </div>

                {/* Reaction Zones (new presentation) */}
                {reactionZones.length > 0 && (
                  <div className="mt-6">
                    <h4 className="text-xs font-bold text-slate-400 uppercase mb-3">Zone principali di reazione del prezzo</h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      {reactionZones.map((zone, idx) => {
                        const defaultLabels = [
                          'Reazione immediata',
                          'Ritracciamento intermedio',
                          'Ritracciamento massimo per mantenere il trend'
                        ];
                        const label = zone?.label || defaultLabels[idx] || `Zona ${idx + 1}`;
                        const low = zone?.low ?? '–';
                        const high = zone?.high ?? '–';
                        const strength = zone?.strength_percent ?? null;
                        const kinds = Array.isArray(zone?.components)
                          ? zone.components.map(c => c?.kind).filter(Boolean)
                          : [];
                        const uniqueKinds = [...new Set(kinds)];
                        const kindLabel = uniqueKinds.length > 1
                          ? `Confluenza: ${uniqueKinds.join(' + ')}`
                          : (uniqueKinds[0] ? uniqueKinds[0] : null);

                        return (
                          <div key={idx} className="bg-slate-800/40 rounded-xl p-4 border border-slate-700/50">
                            <div className="flex items-start justify-between gap-3 mb-2">
                              <div className="min-w-0">
                                <div className="text-xs text-slate-400 uppercase tracking-wider font-semibold">Zona {idx + 1}</div>
                                <div className="text-sm font-bold text-white truncate">{label}</div>
                              </div>
                              {strength != null && (
                                <div className="px-2.5 py-1 rounded-lg bg-violet-500/15 border border-violet-500/25 text-violet-200 text-xs font-bold">
                                  {strength}%
                                </div>
                              )}
                            </div>

                            <div className="text-sm text-slate-300 font-mono mb-2">
                              {String(low)} – {String(high)}
                            </div>

                            {kindLabel && (
                              <div className="text-xs text-slate-400 mb-2">
                                {kindLabel}
                              </div>
                            )}

                            <div className="grid grid-cols-2 gap-2 text-xs">
                              {zone?.distance_pips != null && (
                                <div className="bg-slate-900/50 rounded-lg p-2 border border-slate-700/40">
                                  <span className="text-slate-500">Distanza</span>
                                  <div className="text-slate-200 font-semibold">{zone.distance_pips} pips</div>
                                </div>
                              )}
                              {zone?.source_timeframe && (
                                <div className="bg-slate-900/50 rounded-lg p-2 border border-slate-700/40">
                                  <span className="text-slate-500">Timeframe</span>
                                  <div className="text-slate-200 font-semibold">{zone.source_timeframe}</div>
                                </div>
                              )}
                              {zone?.anchor_level != null && (
                                <div className="bg-slate-900/50 rounded-lg p-2 border border-slate-700/40 col-span-2">
                                  <span className="text-slate-500">Anchor level</span>
                                  <div className="text-slate-200 font-semibold font-mono">{String(zone.anchor_level)}</div>
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Setup development (new presentation) */}
                {setupDevelopmentLines.length > 0 && (
                  <div className="mt-6">
                    <h4 className="text-xs font-bold text-slate-400 uppercase mb-3">
                      {aiAnalysis?.setup_development?.title || 'Possibile sviluppo del setup'}
                    </h4>
                    <div className="bg-slate-800/30 rounded-xl p-4 border border-slate-700/50">
                      <ul className="space-y-2">
                        {setupDevelopmentLines.map((line, i) => (
                          <li key={i} className="flex items-start gap-2 text-sm text-slate-300">
                            <CheckCircle className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" />
                            <span>{line}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                )}

                {/* Invalidation – livello coerente con Stop invalidazione (Scenario 2) */}
                {showInvalidationSection && (
                  <div className="mt-6">
                    <h4 className="text-xs font-bold text-slate-400 uppercase mb-3">
                      {aiAnalysis?.invalidation?.title || 'Invalidazione del setup'}
                    </h4>
                    <div className="bg-red-500/10 rounded-xl p-4 border border-red-500/20">
                      {invalidationRules.length > 0 && (
                        <ul className="space-y-2">
                          {invalidationRules.map((rule, i) => (
                            <li key={i} className="flex items-start gap-2 text-sm text-slate-200">
                              <AlertCircle className="w-4 h-4 text-red-300 flex-shrink-0 mt-0.5" />
                              <span>{rule}</span>
                            </li>
                          ))}
                        </ul>
                      )}
                      {invalidationLevel != null && invalidationLevel !== '' && (
                        <div className={`${invalidationRules.length > 0 ? 'mt-4 ' : ''}bg-slate-900/40 rounded-lg p-3 border border-red-500/20`}>
                          <div className="text-xs text-slate-400 uppercase tracking-wider mb-1">{t('stop_invalidation') || 'Stop invalidazione'}</div>
                          <div className="text-sm font-bold text-red-200 font-mono">{String(invalidationLevel)}</div>
                          {t('stop_invalidation_desc') && <div className="text-xs text-slate-400 mt-1">{t('stop_invalidation_desc')}</div>}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Key Points */}
                {aiAnalysis.key_points && aiAnalysis.key_points.length > 0 && (
                  <div className="space-y-2">
                    <h4 className="text-xs font-bold text-slate-400 uppercase">{t('key_points')}</h4>
                    {aiAnalysis.key_points.map((point, i) => (
                      <div key={i} className="flex items-start gap-2 text-sm">
                        <CheckCircle className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" />
                        <span className="text-slate-300">{point}</span>
                      </div>
                    ))}
                  </div>
                )}

                {/* Risks */}
                {aiAnalysis.risks && aiAnalysis.risks.length > 0 && (
                  <div className="space-y-2 mt-4">
                    <h4 className="text-xs font-bold text-slate-400 uppercase">{t('risks_to_consider')}</h4>
                    {aiAnalysis.risks.map((risk, i) => (
                      <div key={i} className="flex items-start gap-2 text-sm">
                        <AlertCircle className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
                        <span className="text-slate-300">{risk}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Save Signal Button */}
              {aiAnalysis.can_save && (
                <button
                  onClick={saveSignal}
                  disabled={isSaving || saveSuccess}
                  className={`w-full py-3.5 rounded-lg font-bold text-white flex items-center justify-center gap-2.5 transition-smooth active:scale-[0.98] ${saveSuccess
                    ? 'bg-green-600 cursor-default shadow-medium shadow-green-500/20'
                    : 'bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 shadow-medium shadow-blue-500/25 hover:shadow-strong hover:shadow-blue-500/30'
                    } disabled:opacity-50`}
                >
                  {isSaving ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      {t('saving')}
                    </>
                  ) : saveSuccess ? (
                    <>
                      <CheckCircle className="w-5 h-5" />
                      {t('signal_saved')}
                    </>
                  ) : (
                    <>
                      <Target className="w-5 h-5" />
                      💾 {t('save_to_history')}
                    </>
                  )}
                </button>
              )}

              {/* Refresh Button */}
              <button
                onClick={() => {
                  setAiAnalysis(null);
                  requestAIAnalysis();
                }}
                disabled={isAnalyzing}
                className="w-full py-2.5 bg-slate-800/60 text-slate-300 rounded-lg text-sm font-medium flex items-center justify-center gap-2 hover:bg-slate-700/80 transition-smooth active:scale-[0.98] border border-slate-700/40 hover:border-slate-600/50"
              >
                <RefreshCw className={`w-4 h-4 ${isAnalyzing ? 'animate-spin' : ''}`} />
                {t('regenerate_analysis')}
              </button>
            </div>
          )}
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="glass-card p-4 bg-red-500/10 border border-red-500/30">
          <div className="flex items-center gap-2">
            <AlertCircle className="w-5 h-5 text-red-400" />
            <span className="text-sm text-red-400">{error}</span>
          </div>
        </div>
      )}
    </div>
  );
}
